/**
 * Warden: tank (PLAN §4), rotation rework (docs/design/classes/warden.md, rotation-rework.md §4.1).
 * "A shield wall that hits back." Every button is an instant; the rhythm comes from procs, resets,
 * charges and one off-GCD transform:
 *
 *   dodge / parry / block, Cleave Strike, Consecrated Ground ─► Opening (2 stacks) ─► Riposte (+75%, +1 Bastion)
 *   Riposte ─► Shield Momentum ─► resets Shield Slam (+25%, +10 Resolve)
 *   Shield Slam ─► Aegis Rebound ─► resets Aegis Throw (+50%, +1 bounce)
 *   Aegis Throw ─► key 5 becomes Aegis Recall (off-GCD catch) for 6 s
 *   Slam / Aegis (max 2) / Recall (1) / Riposte / Last Stand ─► Bastion (6 stacks, −1.5% damage taken each)
 *   Bulwark Strike cashes every Bastion: heal × (1 + 0.2 per stack)
 *
 * Short window: Consecrated Ground (20 s) grants Opening and Aegis Rebound. Major window: Last Stand
 * (90 s) sets Bastion to 6 and gives 30 Resolve for an immediate 29% Bulwark Strike. Bulwark costs 25 Resolve,
 * so it can be cashed at 4–5 Bastion instead of waiting for (and overcapping) 6.
 *
 * Budget: harness tuning pass (2026-10-13) keeps +2 single-target DPS within +2% of the pre-rework kit;
 * the extra Bulwark presses are paid for by Bulwark 1.6, Slam 4.0, Riposte 1.55, Aegis 1.75, Recall 0.9, CG tick 0.3.
 *
 * Every random proc is a ProcSpec on the hidden `warden.rhythm` passive (one ProcSpec per source,
 * so each source has its own bad-luck counter). Rolls use the sim RNG.
 */
import type { AbilityDef, AreaSpec, AuraDef, ClassDef, EffectSpec } from '@keystone/sim';
import { ABILITY, AURA, CLASS, PASSIVE } from '../ids';
import { PLAYER_HP, baseStats } from '../balance';
import { OFF_GCD, aroundTarget, dodge, interrupt, selfBuff } from './common';
import { WARDEN_UNLOCKS } from './unlocks';

const A = ABILITY.warden;

/** Internal (hidden) aura ids. */
const AEGIS_STRUCK = 'warden.aegis_struck';
/** Counts Aegis Throw hits within one throw (caps its Bastion at 2: an AoE throw used to fill the bar alone). */
const THROW_HITS = 'warden.aegis_throw_hits';
/** Counts Aegis Recall hits within one cast (its Bastion is granted on the first hit only; the Avenger capstone reads 2+). */
export const WARDEN_RECALL_HITS = 'warden.aegis_recall_hits';

// ============================================================================
// Procs and stacks (every visible proc is a player buff with ui.proc and a glow)
// ============================================================================

/** Opening: the next Riposte deals +75% and grants +1 extra Bastion. 2 stacks; spend before you cap. */
const opening: AuraDef = {
  id: AURA.opening,
  name: 'Opening',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'stack',
  maxStacks: 2,
  mods: [{ stat: 'damageDonePct', value: 0.75, abilityIds: [A.riposte], ignoreStacks: true }],
  // One stack is spent after Riposte has hit every enemy in its cone.
  procs: [{ on: 'castResolved', abilityIds: [A.riposte], consumeStack: true, effects: [] }],
  ui: { proc: true, stackCounterOn: [A.riposte] },
};

/** Shield Momentum: Shield Slam's cooldown resets; the next Slam deals +25% and gives +10 Resolve. */
const shieldMomentum: AuraDef = {
  id: AURA.shieldMomentum,
  name: 'Shield Momentum',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.25, abilityIds: [A.shieldSlam] }],
  // Twin Slams patches this to `addCharge`.
  onApply: [{ type: 'modifyCooldown', to: 'self', abilityId: A.shieldSlam, reset: true }],
  procs: [{ on: 'castResolved', abilityIds: [A.shieldSlam], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Aegis Rebound: Aegis Throw's cooldown resets; the next throw deals +50% and bounces to 1 more enemy. */
const aegisRebound: AuraDef = {
  id: AURA.aegisRebound,
  name: 'Aegis Rebound',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [{ stat: 'damageDonePct', value: 0.5, abilityIds: [A.aegisThrow] }],
  onApply: [{ type: 'modifyCooldown', to: 'self', abilityId: A.aegisThrow, reset: true }],
  procs: [{ on: 'castResolved', abilityIds: [A.aegisThrow], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Aegis Recall (transform): while up, key 5 casts Aegis Recall instead of Aegis Throw. */
const aegisRecallReady: AuraDef = {
  id: AURA.aegisRecallReady,
  name: 'Aegis Recall',
  kind: 'buff',
  durationMs: 6000,
  stacking: 'refresh',
  overrides: [{ abilityId: A.aegisThrow, with: A.aegisRecall }],
};

/** Bastion: −1.5% damage taken per stack (6 max). Bulwark Strike cashes them all; it glows at 6. */
const bastion: AuraDef = {
  id: AURA.bastion,
  name: 'Bastion',
  kind: 'buff',
  durationMs: 20000,
  stacking: 'stack',
  maxStacks: 6,
  mods: [{ stat: 'damageTakenPct', value: -0.015 }],
  ui: { glow: { abilityIds: [A.bulwarkStrike], atStacks: 6 }, stackCounterOn: [A.bulwarkStrike] },
};

/** Hidden marker on enemies the Aegis already hit this throw (so the Rebound bounce finds a new one). */
const aegisStruck: AuraDef = {
  id: AEGIS_STRUCK,
  name: 'Aegis Struck',
  kind: 'debuff',
  durationMs: 500,
  stacking: 'refresh',
  flags: { hidden: true },
};

/** Hidden per-throw hit counter for Aegis Throw. */
const throwHits: AuraDef = {
  id: THROW_HITS,
  name: 'Aegis Throw hits',
  kind: 'buff',
  durationMs: 1000,
  stacking: 'stack',
  maxStacks: 2,
  flags: { hidden: true },
};

/** Hidden per-cast hit counter for Aegis Recall. */
const recallHits: AuraDef = {
  id: WARDEN_RECALL_HITS,
  name: 'Aegis Recall hits',
  kind: 'buff',
  durationMs: 1000,
  stacking: 'stack',
  maxStacks: 3,
  flags: { hidden: true },
};

/**
 * Self-applied aura grant. Each grant carries its own copy of the def: progression's transforms
 * edit every inline occurrence in place, so shared objects would be edited once per reference.
 */
const gain = (aura: AuraDef, stacks?: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura: structuredClone(aura), ...(stacks ? { stacks } : {}) });
const hasOpening = { type: 'selfHasAura', auraId: AURA.opening } as const;

// ============================================================================
// Abilities
// ============================================================================

const shieldSlam: AbilityDef = {
  id: A.shieldSlam,
  name: 'Shield Slam',
  school: 'physical',
  description:
    'Slam the target for heavy damage and high threat. Generates 15 Resolve and 1 Bastion. 2% chance to grant Aegis Rebound. Deals 30% more during Shield Wall Bash; Shield Momentum resets it.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cooldown: { durationMs: 6000 },
  gain: [{ resource: 'resolve', amount: 15 }],
  flatThreat: 1500,
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 4.0, variance: 0.05 } },
    gain(bastion),
    // Shield Momentum: +10 Resolve on the empowered Slam (the +25% is the aura's mod).
    { type: 'conditional', cond: { type: 'selfHasAura', auraId: AURA.shieldMomentum }, then: [{ type: 'resource', to: 'self', resource: 'resolve', amount: 10 }] },
  ],
  ui: { tags: ['builder'], color: '#6d8fd6' },
};

const cleaveStrike: AbilityDef = {
  id: A.cleaveStrike,
  name: 'Cleave Strike',
  school: 'physical',
  description: 'Strike up to 5 enemies near the target. Double threat. Generates 5 Resolve. 35% chance to grant Opening.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  gain: [{ resource: 'resolve', amount: 5 }],
  effects: [{ type: 'damage', to: 'area', area: aroundTarget(8, 5), school: 'physical', amount: { coef: 1.7, variance: 0.05 }, threatMult: 2 }],
  ui: { tags: ['builder', 'aoe'], color: '#6d8fd6' },
};

const bulwarkStrike: AbilityDef = {
  id: A.bulwarkStrike,
  name: 'Bulwark Strike',
  school: 'physical',
  description:
    'Strike the target and heal for 13% of the damage taken in the last 5 s (min 7% max HP), +20% per Bastion. Consumes all Bastion (29% at 6).',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'resolve', amount: 25 }],
  effects: [
    { type: 'damage', school: 'physical', amount: { coef: 1.6, variance: 0.05 } },
    {
      type: 'heal',
      to: 'self',
      amount: { pctRecentDamageTaken: { pct: 0.13, windowMs: 5000, minPctMaxHp: 0.07 }, perStack: { auraId: AURA.bastion, on: 'self', mult: 0.2 } },
    },
    { type: 'removeAura', to: 'self', auraId: AURA.bastion },
  ],
  ui: { tags: ['spender', 'heal', 'defensive'], color: '#c84b4b' },
};

const consecratedGround: AbilityDef = {
  id: A.consecratedGround,
  name: 'Consecrated Ground',
  school: 'holy',
  description:
    'Consecrate the ground for 12 s: damages enemies inside, and you take 10% less damage while inside. Grants Opening and Aegis Rebound (resets Aegis Throw).',
  targeting: 'none',
  cooldown: { durationMs: 20000 },
  effects: [
    {
      type: 'zone',
      zoneId: 'warden.consecrated_ground',
      anchor: 'caster',
      shape: { type: 'circle', radius: 8 },
      durationMs: 12000,
      affects: 'enemies',
      tick: { intervalMs: 1000, effects: [{ type: 'damage', school: 'holy', amount: { coef: 0.3 }, threatMult: 2, isAoE: true }] },
    },
    {
      // Damage reduction is Warden-only: a caster-only refreshing aura while standing inside.
      type: 'zone',
      zoneId: 'warden.consecrated_ground_ward',
      anchor: 'caster',
      shape: { type: 'circle', radius: 8 },
      durationMs: 12000,
      affects: 'allies',
      tick: {
        intervalMs: 500,
        effects: [
          {
            type: 'applyAura',
            if: { type: 'targetIsSelf' },
            aura: {
              id: AURA.consecratedGround,
              name: 'Consecrated Ground',
              kind: 'buff',
              durationMs: 750,
              stacking: 'refresh',
              mods: [{ stat: 'damageTakenPct', value: -0.1 }],
            },
          },
        ],
      },
    },
    // The short burst window: CG → Aegis Throw (+ Recall) → Riposte → Slam.
    gain(opening),
    gain(aegisRebound),
  ],
  ui: { tags: ['aoe', 'defensive'], color: '#e8c95a' },
};

/**
 * Aegis bounce area: the primary target and up to 2 more enemies within 12 yd of it. A fresh object
 * per effect: talents (Ricochet's modifyArea) edit each effect's area in place.
 */
const aegisArea = (): AreaSpec => aroundTarget(12, 3);
/** Aegis Rebound's extra bounce: the next enemy near the target that this throw has not hit yet. */
const aegisReboundArea = (): AreaSpec => ({ anchor: 'target', shape: { type: 'circle', radius: 12 }, affects: 'enemies', maxTargets: 3, excludePrimary: true });

const aegisThrow: AbilityDef = {
  id: A.aegisThrow,
  name: 'Aegis Throw',
  school: 'holy',
  description:
    'Hurl your shield at the target. It bounces to 2 more enemies within 12 yd and interrupts the target. +10 Resolve and +1 Bastion per enemy hit (max 2). Key 5 becomes Aegis Recall for 6 s.',
  targeting: 'enemy',
  range: 30,
  requiresFacing: true,
  cooldown: { durationMs: 15000 },
  gain: [{ resource: 'resolve', amount: 10 }],
  flatThreat: 800,
  effects: [
    interrupt(3000),
    { type: 'damage', to: 'area', area: aegisArea(), school: 'holy', amount: { coef: 1.75, variance: 0.05 } },
    { type: 'applyAura', to: 'area', area: aegisArea(), aura: structuredClone(aegisStruck) },
    {
      // Aegis Rebound: one more bounce (the nearest enemy not struck yet). Ricochet's +2 targets
      // widen all three areas together, so the bounce stays "the next new enemy".
      type: 'damage',
      to: 'area',
      area: aegisReboundArea(),
      if: { type: 'and', all: [{ type: 'selfHasAura', auraId: AURA.aegisRebound }, { type: 'targetLacksAura', auraId: AEGIS_STRUCK, fromSelf: true }] },
      school: 'holy',
      amount: { coef: 1.75, variance: 0.05 },
    },
    gain(aegisRecallReady),
  ],
  ui: {
    tags: ['builder', 'aoe'],
    color: '#e8c95a',
    // The Rebound bounce is gated on a hidden marker, which the generated tooltip cannot phrase.
    desc: 'Hurl your shield at the target, interrupting its spellcasting and dealing {damage} Holy damage to it and up to two more enemies nearby. With Aegis Rebound, the shield bounces to one more enemy it has not hit yet.',
  },
};

const aegisRecall: AbilityDef = {
  id: A.aegisRecall,
  name: 'Aegis Recall',
  school: 'holy',
  description:
    'Off the GCD: call your shield back. It strikes every enemy in a 3 yd wide line between you and your target (double threat). +5 Resolve, and +1 Bastion if it hits.',
  targeting: 'enemy',
  range: 30,
  gcdMs: OFF_GCD,
  // Only through the key-5 transform: the raw id is never castable on its own.
  usableIf: { type: 'selfHasAura', auraId: AURA.aegisRecallReady },
  gain: [{ resource: 'resolve', amount: 5 }],
  effects: [
    {
      type: 'damage',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'line', length: 30, width: 3 }, affects: 'enemies', includePrimary: true },
      school: 'holy',
      amount: { coef: 0.9, variance: 0.05 },
      threatMult: 2,
      isAoE: true,
    },
    { type: 'removeAura', to: 'self', auraId: AURA.aegisRecallReady },
  ],
  ui: { tags: ['aoe'], color: '#e8c95a', animAs: A.aegisThrow },
};

const riposte: AbilityDef = {
  id: A.riposte,
  name: 'Riposte',
  school: 'physical',
  description:
    'Strike up to 5 enemies in a frontal cone (double threat). Grants 1 Bastion. With Opening: +75% damage and 1 more Bastion. 30% chance to grant Shield Momentum.',
  targeting: 'enemy',
  melee: true,
  requiresFacing: true,
  cost: [{ resource: 'resolve', amount: 20 }],
  effects: [
    {
      type: 'damage',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'cone', range: 8, angle: (2 * Math.PI) / 3 }, affects: 'enemies', maxTargets: 5, includePrimary: true },
      school: 'physical',
      amount: { coef: 1.55, variance: 0.05 },
      threatMult: 2,
    },
    gain(bastion),
    { type: 'conditional', cond: hasOpening, then: [gain(bastion)] },
  ],
  ui: { tags: ['spender', 'aoe'], color: '#9fb6e8' },
};

const lastStand: AbilityDef = {
  id: A.lastStand,
  name: 'Last Stand',
  school: 'physical',
  description: 'Reduce damage taken by 40% for 8 s. Generates 30 Resolve and sets Bastion to 6: follow with Bulwark Strike.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90000 },
  gain: [{ resource: 'resolve', amount: 30 }],
  effects: [selfBuff(AURA.lastStand, 'Last Stand', 8000, [{ stat: 'damageTakenPct', value: -0.4 }]), gain(bastion, 6)],
  ui: { tags: ['majorDefensive', 'cooldown'], color: '#9aa7c7' },
};

const shieldWallBash: AbilityDef = {
  id: A.shieldWallBash,
  name: 'Shield Wall Bash',
  school: 'physical',
  description: 'Off the GCD: reduce physical damage taken by 30% and block 20% more often for 6 s. Shield Slam deals 30% more meanwhile. 2 charges.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 18000, charges: 2 },
  effects: [
    selfBuff(
      AURA.shieldWallBash,
      'Shield Wall Bash',
      6000,
      [
        { stat: 'damageTakenPct', value: -0.3, school: 'physical' },
        { stat: 'block', value: 0.2 },
        { stat: 'damageDonePct', value: 0.3, abilityIds: [A.shieldSlam] },
      ],
      { pandemic: true, ui: { glow: { abilityIds: [A.shieldSlam] } } },
    ),
  ],
  ui: { tags: ['defensive'], color: '#9aa7c7' },
};

const shieldBash: AbilityDef = {
  id: A.shieldBash,
  name: 'Shield Bash',
  school: 'physical',
  description: 'Interrupt the target and lock its school for 4 s.',
  targeting: 'enemy',
  melee: true,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 15000 },
  effects: [interrupt(4000), { type: 'damage', school: 'physical', amount: { coef: 0.5 } }],
  ui: { tags: ['interrupt'], color: '#ffffff' },
};

const taunt: AbilityDef = {
  id: A.taunt,
  name: 'Taunt',
  school: 'physical',
  description: 'Force the target to attack you for 3 s.',
  targeting: 'enemy',
  range: 30,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 8000 },
  effects: [{ type: 'taunt', durationMs: 3000 }],
  ui: { tags: ['taunt'], color: '#ff9b3d' },
};

const grip: AbilityDef = {
  id: A.grip,
  name: 'Grip',
  school: 'shadow',
  description: 'Pull the target to you and taunt it.',
  targeting: 'enemy',
  range: 30,
  cooldown: { durationMs: 25000 },
  effects: [{ type: 'grip', stopAt: 3 }, { type: 'taunt', durationMs: 3000 }],
  ui: { tags: ['taunt', 'movement'], color: '#ff9b3d' },
};

// ============================================================================
// Hidden passive: every proc roll and per-hit Bastion grant
// ============================================================================

const rhythm: AuraDef = {
  id: PASSIVE.wardenRhythm,
  name: "Warden's Rhythm",
  kind: 'buff',
  durationMs: 0,
  stacking: 'unique',
  flags: { hidden: true, persistThroughDeath: true },
  procs: [
    // Opening (a): you dodge, parry or block a melee attack.
    { on: 'avoided', chance: 0.12, icdMs: 4000, badLuck: { step: 0.03 }, effects: [gain(opening)] },
    // Opening (b): Cleave Strike.
    { on: 'castResolved', abilityIds: [A.cleaveStrike], chance: 0.35, badLuck: { step: 0.1 }, effects: [gain(opening)] },
    // Shield Momentum: Riposte.
    { on: 'castResolved', abilityIds: [A.riposte], chance: 0.3, badLuck: { step: 0.1 }, effects: [gain(shieldMomentum)] },
    // Aegis Rebound: Shield Slam.
    { on: 'castResolved', abilityIds: [A.shieldSlam], chance: 0.02, badLuck: { step: 0.01 }, effects: [gain(aegisRebound)] },
    // Bastion per enemy the Aegis hits, at most 2 per throw.
    {
      on: 'damageDealt',
      abilityIds: [A.aegisThrow],
      if: { type: 'not', cond: { type: 'selfHasAura', auraId: THROW_HITS, minStacks: 2 } },
      effects: [gain(bastion), gain(throwHits)],
    },
    // Aegis Recall hits after the first are counted here (the Avenger capstone reads 2+). This proc
    // must stay before the Bastion one below, which counts the first hit.
    { on: 'damageDealt', abilityIds: [A.aegisRecall], if: { type: 'selfHasAura', auraId: WARDEN_RECALL_HITS }, effects: [gain(recallHits)] },
    // Aegis Recall: 1 Bastion per cast (on its first hit).
    {
      on: 'damageDealt',
      abilityIds: [A.aegisRecall],
      if: { type: 'not', cond: { type: 'selfHasAura', auraId: WARDEN_RECALL_HITS, minStacks: 1 } },
      effects: [gain(bastion), gain(recallHits)],
    },
  ],
};

export const warden: ClassDef = {
  id: CLASS.warden,
  name: 'Warden',
  role: 'tank',
  resources: [
    {
      id: 'resolve',
      name: 'Resolve',
      max: 100,
      start: 0,
      regenPerSecOoc: -5,
      gainOn: [{ on: 'damageTaken', amount: 1, perPctMaxHp: true }],
    },
  ],
  binds: {
    '1': A.shieldSlam,
    '2': A.cleaveStrike,
    '3': A.bulwarkStrike,
    '4': A.consecratedGround,
    Q: A.lastStand,
    E: A.shieldWallBash,
    R: A.shieldBash,
    F: A.taunt,
  },
  extraBinds: {
    '5': A.aegisThrow,
    '6': A.riposte,
    G: A.grip,
  },
  abilities: [shieldSlam, cleaveStrike, bulwarkStrike, consecratedGround, aegisThrow, riposte, lastStand, shieldWallBash, shieldBash, taunt, grip, aegisRecall, dodge],
  // Grip (G) stays here for the alt-F fallback; Aegis Recall is reachable only through the key-5 transform.
  extraAbilities: [A.grip, A.aegisRecall, ABILITY.common.dodge],
  baseStats: baseStats({ armor: 0.3, dodge: 0.1, parry: 0.1, block: 0.08, blockAmount: 0.3 }),
  maxHp: PLAYER_HP.tank,
  moveSpeed: 7,
  melee: { damage: { coef: 1.5, variance: 0.05 }, speedMs: 2600, school: 'physical' },
  passives: [
    {
      id: AURA.wardenThreat,
      name: 'Warden of the Line',
      kind: 'buff',
      durationMs: 0,
      stacking: 'unique',
      mods: [{ stat: 'threatPct', value: 3 }], // ×4 threat
      flags: { hidden: true, persistThroughDeath: true },
    },
    rhythm,
  ],
  color: '#4a7bd1',
  // +field: hero-level ability gates (unlocks.ts).
  unlocks: WARDEN_UNLOCKS,
};
